import { customElement, BoltElement, html } from '@bolt/element';
import {
  tippy,
  hideOnEsc,
  handleFocus,
  inspectNodeTypes,
  getTallestStickyHeight,
} from '@bolt/core/utils';
import schema from '../popover.schema';
import '@bolt/core/elements/focus-trap';

@customElement('bolt-popover')
class BoltPopover extends BoltElement {
  static schema = schema;

  static get properties() {
    return {
      ...this.props,
      zIndex: { type: Number },
      paddingTop: { type: Number },
    };
  }

  constructor() {
    super();
    this.uuid = this.uuid || Math.floor(10000 + Math.random() * 90000);
  }

  disconnectedCallback() {
    super.disconnectedCallback && super.disconnectedCallback();

    this.tippyInstance?.hide();
  }

  getPaddingTop() {
    const stickyPageHeader =
      document.querySelector('.c-bolt-page-header') ||
      document.querySelector('.c-page-header.is-fixed'); // use [data-sticky-page-header] once navbar work merged into master

    let offsetHeight = 0;

    if (stickyPageHeader) {
      offsetHeight += stickyPageHeader.offsetHeight;
    }

    return offsetHeight + getTallestStickyHeight();
  }

  initTippy() {
    this.zIndex = parseInt(
      getComputedStyle(this).getPropertyValue('--c-bolt-popover-z-index'),
      10,
    );
    this.paddingTop = this.getPaddingTop();
    this.$boundary =
      this.$boundary ||
      (this.boundary && this.closest(this.boundary)) ||
      undefined;

    // The <dialog> element sits in a separate layer above the <body>.
    // Append to <dialog> element or popover will not show up.
    this.$targetElement = this.closest('dialog')
      ? this.closest('dialog')
      : document.body;

    // Note: trigger cannot not be a shadow DOM element or Tippy doesn't always hide properly
    this.tippyInstance = tippy(this.trigger, {
      content: this.content,
      placement: this.placement || schema.properties.placement.default,
      trigger: this.triggerEvent === 'hover' ? 'mouseenter click' : 'click',
      arrow: false,
      interactive: true,
      theme: 'popover',
      role: null,
      appendTo: this.$targetElement,
      maxWidth: 'none', // Set width via CSS variable, requires legacy Edge support
      offset: [0, 0],
      plugins: [hideOnEsc, handleFocus],
      zIndex: this.zIndex,
      popperOptions: {
        modifiers: [
          {
            name: 'flip',
            options: {
              fallbackPlacements: this.fallbackPlacements ?? undefined,
              boundary: this.$boundary,
              padding: { top: this.paddingTop },
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: this.$boundary,
            },
          },
        ],
      },
      onCreate: ({ popper }) => {
        if (this.theme) {
          popper
            .querySelector('.tippy-box')
            .classList.add(`t-bolt-${this.theme}`);
        }
      },
    });
  }

  firstUpdated() {
    const { firstFocusableElement } = inspectNodeTypes(
      this.slotMap.get('default'),
    );

    // If `firstFocusableElement` has Shadow DOM, `getFocusableElement()` returns the focusable element within the Shadow DOM
    this.trigger = firstFocusableElement || this;
    this.content = this.querySelector('[slot="content"]');

    if (!this.content) return;

    // Handle non-focusable trigger elements
    if (!firstFocusableElement) {
      this.trigger.setAttribute('role', 'button');
      this.trigger.setAttribute('tabindex', 0);
    }

    // Handle popup/menu inside content
    if (this.querySelector('bolt-menu')) {
      this.trigger.setAttribute('aria-haspopup', true);
    } else {
      this.trigger.setAttribute(
        'aria-controls',
        `js-bolt-popover-${this.uuid}`,
      );
      this.content.setAttribute('id', `js-bolt-popover-${this.uuid}`);
    }

    // Set classes on content container
    const contentClasslist = [
      `c-bolt-popover__content`,
      `c-bolt-popover__content--spacing-${this.spacing}`,
    ];
    const textContentLength = this.content.textContent.trim().length;
    if (textContentLength > 31) {
      contentClasslist.push(`c-bolt-popover__content--text-wrap`);
    }
    this.content.classList.add(...contentClasslist);

    // Set width on content container
    const widthCSSVarName = '--c-bolt-popover-bubble-width';
    const widthCSSVarValue =
      getComputedStyle(this).getPropertyValue(widthCSSVarName);
    if (widthCSSVarValue) {
      this.content.style.setProperty(widthCSSVarName, widthCSSVarValue);
    }

    // Add focus trap
    const trap = document.createElement('focus-trap');
    while (this.content.firstChild) {
      trap.appendChild(this.content.firstChild);
    }
    this.content.appendChild(trap);

    this.initTippy();
    this.setAttribute('ready', '');

    // close popover when inner action is clicked
    for (const innerAction of this.content.querySelectorAll('a, button')) {
      innerAction.addEventListener('click', () => {
        this.tippyInstance?.hide();
      });
    }

    // Call super and dispatch "ready" event _after_ Tippy is setup
    super.firstUpdated && super.firstUpdated();
  }

  render() {
    // Remove line breaks before and after lit-html template tags, causes unwanted space inside and around inline links
    // prettier-ignore
    return html`${this.slotify('default')}${this.slotify('content')}`;
  }
}

export { BoltPopover };
